/**
 * Zugang zum eigenen Server.
 *
 * Der Zugangstoken des Beirats steckt in der URL (`?zugang=…`) und wird von
 * hier aus bei jeder Anfrage mitgeschickt — nicht im localStorage, damit ein
 * geteilter Rechner keinen fremden Zugang behält.
 */

import { DEMO, DEMO_PRUEFUNG_ID, demoApi } from '../demo/modus';
import type {
  Fundstelle,
  PdfRechteck,
  Banktransaktion,
  Beleg,
  Dokument,
  EingangAktion,
  Eingangsposten,
  Gruppe,
  Hervorhebungen,
  Journal,
  Journalkontrolle,
  Kategorie,
  Kennzahlen,
  Kommentar,
  Konto,
  Kontoauszug,
  Mandant,
  Mitarbeiter,
  Mitglied,
  Pruefung,
  PruefStatus,
  PruefungKurz,
  Rolle,
  Saldenzeile,
  Schritt,
  SchrittStatus,
  Zahlungsbefund,
  Zuordnung,
} from './typen';

export class ApiFehler extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiFehler';
  }
}

/** Liest das Zugangstoken aus der Adresszeile. */
export function zugangToken(): string | null {
  return new URLSearchParams(window.location.search).get('zugang');
}

/**
 * Der Mandant, in dem ein Betreiber gerade arbeitet.
 *
 * Nur für Systemadmins von Bedeutung; ein Mitarbeiter einer Hausverwaltung
 * hat genau einen. Im sessionStorage, damit ein Neuladen die Wahl behält —
 * eine Bequemlichkeit, keine Wahrheit: der Server prüft ohnehin.
 */
export function gewaehlterMandant(): string | null {
  try {
    return sessionStorage.getItem('bp_mandant');
  } catch {
    return null;
  }
}

export function mandantWaehlen(id: string | null): void {
  try {
    if (id) sessionStorage.setItem('bp_mandant', id);
    else sessionStorage.removeItem('bp_mandant');
  } catch {
    // Ohne Speicher gilt die Wahl bis zum Neuladen.
  }
}

export function pruefungIdAusUrl(): string | null {
  // Der Klickdummy kennt genau eine Prüfung und wird oft als lokale Datei
  // geöffnet — dort gibt es keinen Pfad, aus dem sich etwas lesen ließe.
  if (DEMO) return DEMO_PRUEFUNG_ID;
  const treffer = /\/pruefung\/([0-9a-f-]{36})/i.exec(window.location.pathname);
  return treffer ? treffer[1] : null;
}

async function anfrage<T>(pfad: string, optionen: RequestInit = {}): Promise<T> {
  const token = zugangToken();
  const kopf = new Headers(optionen.headers);
  if (token) kopf.set('x-zugang', token);
  const mandant = gewaehlterMandant();
  if (mandant) kopf.set('x-mandant', mandant);
  if (optionen.body && !(optionen.body instanceof FormData)) {
    kopf.set('Content-Type', 'application/json');
  }

  const antwort = await fetch(`/api${pfad}`, { ...optionen, headers: kopf });

  if (!antwort.ok) {
    let nachricht = `Die Anfrage ist fehlgeschlagen (HTTP ${antwort.status}).`;
    let code: string | undefined;
    try {
      const daten = (await antwort.json()) as { fehler?: string; code?: string };
      if (daten.fehler) nachricht = daten.fehler;
      code = daten.code;
    } catch {
      // Antwort ohne JSON — die Standardnachricht trägt.
    }
    throw new ApiFehler(nachricht, antwort.status, code);
  }

  if (antwort.status === 204) return undefined as T;
  return (await antwort.json()) as T;
}

/**
 * Im Klickdummy wird jede Anfrage im Arbeitsspeicher beantwortet statt über
 * das Netz. Die Weiche steht hier und nur hier — die Seiten der Oberfläche
 * merken vom Unterschied nichts.
 */
function gleichDemo<T>(wert: T): Promise<T> {
  return Promise.resolve(wert);
}

export const api = {
  pruefung: (id: string) =>
    DEMO ? demoApi.pruefung() : anfrage<{ pruefung: Pruefung; rolle: Rolle }>(`/pruefungen/${id}`),

  kennzahlen: (id: string) =>
    DEMO ? demoApi.kennzahlen() : anfrage<Kennzahlen>(`/pruefungen/${id}/kennzahlen`),

  belege: (id: string) =>
    DEMO
      ? demoApi.belege()
      : anfrage<{ belege: Beleg[]; gliederung: Gruppe[] }>(`/pruefungen/${id}/belege`),

  belegeLaden: (id: string) =>
    anfrage<{ geladen: number; stand: string }>(`/pruefungen/${id}/belege/laden`, {
      method: 'POST',
    }),

  statusSetzen: (belegId: string, pruefungId: string, status: PruefStatus) =>
    DEMO
      ? demoApi.statusSetzen(belegId, status)
      : anfrage<{ ok: true }>(`/belege/${belegId}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ pruefungId, status }),
        }),

  /** Die Feldkoordinaten für das Highlighting. Leeres Objekt = gescannter Beleg. */
  hervorhebungen: (belegId: string) =>
    // Im Klickdummy zeichnet das Belegblatt die Hervorhebung selbst.
    DEMO
      ? Promise.resolve({} as Hervorhebungen)
      : anfrage<Hervorhebungen>(`/belege/${belegId}/hervorhebungen`),

  /** Das PDF wird über den eigenen Server geholt, nie direkt aus iDWELL. */
  belegPdfUrl: (belegId: string) => {
    const token = zugangToken();
    return `/api/belege/${belegId}/pdf${token ? `?zugang=${encodeURIComponent(token)}` : ''}`;
  },

  /**
   * Das Prüfprotokoll als PDF. Auch vor dem Abschluss abrufbar — dann steht
   * „Zwischenstand" darin. Im Klickdummy gibt es keinen Server, der es
   * erzeugen könnte; dort bleibt der Knopf verborgen.
   */
  protokollUrl: (pruefungId: string) => {
    const token = zugangToken();
    return `/api/pruefungen/${pruefungId}/protokoll.pdf${
      token ? `?zugang=${encodeURIComponent(token)}` : ''
    }`;
  },

  /** Prüfstatus einer Kontoauszugsposition setzen. */
  transaktionStatus: (transaktionId: string, pruefungId: string, status: PruefStatus) =>
    DEMO
      ? demoApi.transaktionStatus(transaktionId, status)
      : anfrage<{ ok: true }>(`/transaktionen/${transaktionId}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ pruefungId, status }),
        }),

  /** Eine Zuordnung von Hand anlegen. Ohne Betrag wird der offene Rest genommen. */
  verknuepfen: (pruefungId: string, transaktionId: string, belegId: string, betrag?: number) =>
    DEMO
      ? demoApi.verknuepfen(transaktionId, belegId, betrag)
      : anfrage<{ id: string; betrag: number }>(`/pruefungen/${pruefungId}/zuordnungen`, {
          method: 'POST',
          body: JSON.stringify({ transaktionId, belegId, betrag }),
        }),

  /** Einen Vorschlag des Abgleichs bestätigen. */
  zuordnungBestaetigen: (zuordnungId: string) =>
    DEMO
      ? demoApi.zuordnungBestaetigen(zuordnungId)
      : anfrage<{ ok: true }>(`/zuordnungen/${zuordnungId}/bestaetigen`, { method: 'POST' }),

  /** Eine Verknüpfung wieder lösen. */
  zuordnungLoesen: (zuordnungId: string) =>
    DEMO
      ? demoApi.zuordnungLoesen(zuordnungId)
      : anfrage<{ ok: true }>(`/zuordnungen/${zuordnungId}`, { method: 'DELETE' }),

  /** Eine Position als nicht belegpflichtig abhaken. */
  ohneBelegAbhaken: (pruefungId: string, transaktionId: string, begruendung: string) =>
    DEMO
      ? demoApi.ohneBelegAbhaken(transaktionId, begruendung)
      : anfrage<{ ok: true }>(
          `/pruefungen/${pruefungId}/transaktionen/${transaktionId}/ohne-beleg`,
          { method: 'POST', body: JSON.stringify({ begruendung }) },
        ),

  /** Ob der Server Mails verschicken kann. */
  mailStatus: () =>
    DEMO
      ? Promise.resolve({ ok: true, meldung: 'Vorführfassung — es wird nichts verschickt.' })
      : anfrage<{ ok: boolean; meldung: string }>('/mail/status'),

  /** Einen Beirat einladen und den Link verschicken. */
  einladen: (pruefungId: string, email: string, name?: string) =>
    DEMO
      ? demoApi.einladen(email, name)
      : anfrage<{
          name: string;
          email: string;
          link: string;
          gueltigBis: string | null;
          verschickt: boolean;
          meldung: string;
        }>(`/pruefungen/${pruefungId}/einladen`, {
          method: 'POST',
          body: JSON.stringify({ email, name }),
        }),

  /** Zahlungsbefund je Beleg — Grundlage der Sprungnavigation. */
  zahlungen: (id: string) =>
    DEMO
      ? demoApi.zahlungen()
      : anfrage<Record<string, Zahlungsbefund>>(`/pruefungen/${id}/zahlungen`),

  /** Die Objekte des Mandanten für das Auswahlfeld der Einrichtung. */
  objekte: () =>
    anfrage<{ id: string; nummer: string; bezeichnung: string; ort: string | null }[]>(
      '/objekte',
    ),

  /**
   * Eine Prüfung einrichten: Objekt, Jahr, E-Mail des Beirats.
   * Legt an, holt die Belege und gibt den Magic Link zurück — einmal.
   */
  einrichten: (daten: {
    liegenschaftId?: string;
    jahr: number;
    beiratEmail?: string;
    beiratName?: string;
    frist?: string;
    belegquelle?: 'idwell' | 'import';
    bezeichnung?: string;
  }) =>
    anfrage<{
      id: string;
      liegenschaft: { id: string; nummer: string; bezeichnung: string };
      jahr: number;
      belegquelle: 'idwell' | 'import';
      belege: number;
      ohneKontierung: number;
      summe: number;
      einladung: { name: string; email: string; link: string } | null;
    }>('/pruefungen', { method: 'POST', body: JSON.stringify(daten) }),

  /** Die Prüfung freigeben — ab dann prüft der Beirat. */
  freigeben: (id: string, frist: string | null) =>
    anfrage<{ links: unknown[] }>(`/pruefungen/${id}/freigeben`, {
      method: 'POST',
      body: JSON.stringify({ beiraete: [], frist }),
    }),

  bankHochladen: (id: string, dateien: File[]) => {
    if (DEMO) return demoApi.bankHochladen(dateien);
    const form = new FormData();
    for (const d of dateien) form.append('dateien', d);
    return anfrage<{ neu: number; dubletten: number; hinweise: string[] }>(
      `/pruefungen/${id}/bank`,
      { method: 'POST', body: form },
    );
  },

  bankAbgleichen: (id: string) =>
    DEMO
      ? demoApi.bankAbgleichen()
      : anfrage<{
      kennzahlen: {
        transaktionen: number;
        zugeordnet: number;
        vorschlaege: number;
        sammelzahlungen: number;
        quote: number;
        nichtZugeordneterBetrag: number;
      };
      ohneBeleg: string[];
      ohneZahlung: string[];
        }>(`/pruefungen/${id}/bank/abgleichen`, { method: 'POST' }),

  transaktionen: (id: string) =>
    DEMO
      ? demoApi.transaktionen()
      : anfrage<Kontoauszug>(
          `/pruefungen/${id}/transaktionen`,
        ),

  // ── Journal
  //
  // Die Buchungsliste der Hausverwaltung. Sie ist die vollständige Liste,
  // gegen die geprüft wird — der Belegstapel ist nur ihre eine Hälfte.

  journal: (id: string) =>
    DEMO ? demoApi.journal() : anfrage<Journal>(`/pruefungen/${id}/journal`),

  journalHochladen: (id: string, datei: File) => {
    if (DEMO) return demoApi.journalHochladen(datei);
    const form = new FormData();
    form.append('datei', datei);
    return anfrage<{
      dateiname: string;
      zeilen: number;
      kontrolle: Journalkontrolle;
      hinweise: string[];
      zugeordnet: number;
      vorschlaege: number;
      befunde: number;
    }>(`/pruefungen/${id}/journal`, { method: 'POST', body: form });
  },

  /** Eine Journalzeile von Hand mit einem Beleg verknüpfen. */
  journalVerknuepfen: (pruefungId: string, zeileId: string, belegId: string) =>
    DEMO
      ? demoApi.journalVerknuepfen(zeileId, belegId)
      : anfrage<{ ok: true }>(`/pruefungen/${pruefungId}/journal/zuordnungen`, {
          method: 'POST',
          body: JSON.stringify({ zeileId, belegId }),
        }),

  /** Festhalten, dass es zu dieser Buchung keinen Beleg gibt und keinen geben muss. */
  journalOhneBeleg: (pruefungId: string, zeileId: string) =>
    DEMO
      ? demoApi.journalOhneBeleg(zeileId)
      : anfrage<{ ok: true }>(`/pruefungen/${pruefungId}/journal/zuordnungen`, {
          method: 'POST',
          body: JSON.stringify({ zeileId, ohneBeleg: true }),
        }),

  journalVorschlagAnnehmen: (pruefungId: string, zeileId: string) =>
    DEMO
      ? demoApi.journalVorschlagAnnehmen(zeileId)
      : anfrage<{ ok: true }>(
          `/pruefungen/${pruefungId}/journal/zuordnungen/${zeileId}/bestaetigen`,
          { method: 'POST' },
        ),

  journalLoesen: (pruefungId: string, zeileId: string) =>
    DEMO
      ? demoApi.journalLoesen(zeileId)
      : anfrage<{ ok: true }>(`/pruefungen/${pruefungId}/journal/zuordnungen/${zeileId}`, {
          method: 'DELETE',
        }),

  /**
   * Die Anzeigedrehung eines Belegbildes.
   *
   * Gilt für alle: Ein quer eingescannter Beleg liegt für jeden quer, und wer
   * ihn aufrichtet, erspart es den folgenden Prüfern.
   */
  belegDrehen: (belegId: string, pruefungId: string, grad: 0 | 90 | 180 | 270) =>
    DEMO
      ? demoApi.belegDrehen(belegId, grad)
      : anfrage<{ ok: true; drehung: number }>(`/belege/${belegId}/drehung`, {
          method: 'PATCH',
          body: JSON.stringify({ pruefungId, grad }),
        }),

  // ── Die fünf Prüfschritte

  schritte: (id: string) =>
    DEMO
      ? demoApi.schritte()
      : anfrage<{ schritte: Schritt[]; fortschritt: number }>(`/pruefungen/${id}/schritte`),

  schrittSetzen: (id: string, nummer: number, status: SchrittStatus, anmerkung?: string | null) =>
    DEMO
      ? demoApi.schrittSetzen(nummer, status, anmerkung)
      : anfrage<{ schritte: Schritt[]; fortschritt: number }>(`/pruefungen/${id}/schritte/${nummer}`, {
          method: 'PATCH',
          body: JSON.stringify({ status, anmerkung }),
        }),

  // ── Unterlagen

  dokumente: (id: string) =>
    DEMO
      ? demoApi.dokumente()
      : anfrage<{ kategorien: Kategorie[]; dokumente: Dokument[] }>(`/pruefungen/${id}/dokumente`),

  dokumentUrl: (pruefungId: string, dokumentId: string) => {
    const token = zugangToken();
    return `/api/pruefungen/${pruefungId}/dokumente/${dokumentId}/datei${
      token ? `?zugang=${encodeURIComponent(token)}` : ''
    }`;
  },

  /** Prüfstatus und/oder Prüfvermerk des Beirats zu einer Unterlage. */
  dokumentStatus: (pruefungId: string, dokumentId: string, status: PruefStatus | undefined, vermerk?: string | null) =>
    DEMO
      ? demoApi.dokumentStatus(dokumentId, status, vermerk)
      : anfrage<{ ok: true; dokument?: Dokument }>(`/pruefungen/${pruefungId}/dokumente/${dokumentId}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ ...(status !== undefined ? { status } : {}), ...(vermerk !== undefined ? { vermerk } : {}) }),
        }),

  dokumenteHochladen: (pruefungId: string, kategorieId: string, dateien: File[]) => {
    const form = new FormData();
    form.append('kategorieId', kategorieId);
    for (const d of dateien) form.append('dateien', d);
    return anfrage<{ dokumente: Dokument[] }>(`/pruefungen/${pruefungId}/dokumente`, {
      method: 'POST',
      body: form,
    });
  },

  /** Umbenennen und/oder den Menüpunkt wechseln — nur die Verwaltung. */
  dokumentAendern: (pruefungId: string, dokumentId: string, aenderung: { dateiname?: string; kategorieId?: string }) =>
    anfrage<{ dokument: Dokument }>(`/pruefungen/${pruefungId}/dokumente/${dokumentId}`, {
      method: 'PATCH',
      body: JSON.stringify(aenderung),
    }),

  /** Einen Beleg im Kontoauszug-PDF wiederfinden: Rechnungsnummer, Betrag oder Lieferant im Text. */
  fundstelle: (pruefungId: string, belegId: string) =>
    anfrage<{ fundstelle: Fundstelle | null }>(`/pruefungen/${pruefungId}/belege/${belegId}/fundstelle`).then((a) => a.fundstelle),

  /** KI-Abgleich für einen Kontoauszug (erneut) laufen lassen. */
  dokumentKiAbgleich: (pruefungId: string, dokumentId: string) =>
    anfrage<{ belege: number; zugeordnet: number; vorschlaege: number; ohne: number; hinweise: string[]; meldung: string }>(
      `/pruefungen/${pruefungId}/dokumente/${dokumentId}/ki-abgleich`,
      { method: 'POST', body: '{}' },
    ),

  /** Einen einzelnen Beleg mit der KI in den Kontoauszügen suchen. */
  belegKiAbgleich: (pruefungId: string, belegId: string) =>
    anfrage<{ gefunden: boolean; zugeordnet: number; vorschlaege: number; hinweise: string[] }>(`/pruefungen/${pruefungId}/belege/${belegId}/ki-abgleich`, {
      method: 'POST',
      body: '{}',
    }),

  /** Haken beim Kontoauszug: die Kontobewegungen einer Unterlage für den Belegabgleich einlesen oder entfernen. */
  dokumentAbgleich: (pruefungId: string, dokumentId: string, aktiv: boolean) =>
    anfrage<{ ok: true; meldung: string; anzahl: number; dokument: Dokument }>(`/pruefungen/${pruefungId}/dokumente/${dokumentId}/abgleich`, {
      method: 'POST',
      body: JSON.stringify({ aktiv }),
    }),

  dokumentLoeschen: (pruefungId: string, dokumentId: string) =>
    anfrage<{ ok: true }>(`/pruefungen/${pruefungId}/dokumente/${dokumentId}`, { method: 'DELETE' }),

  // ── Der Prüfassistent

  assistent: {
    status: (pruefungId: string) =>
      DEMO ? gleichDemo({ verfuegbar: false, modell: null }) : anfrage<{ verfuegbar: boolean; modell: string | null }>(`/pruefungen/${pruefungId}/assistent`),
    fragen: (
      pruefungId: string,
      daten: { frage: string; verlauf: { rolle: 'nutzer' | 'assistent'; text: string }[]; dokumentId?: string | null; belegId?: string | null },
    ) =>
      anfrage<{ antwort: string }>(`/pruefungen/${pruefungId}/assistent`, {
        method: 'POST',
        body: JSON.stringify(daten),
      }),
  },

  // ── Der Eingang: alle Unterlagen auf einmal

  eingang: {
    hochladen: (pruefungId: string, dateien: File[]) => {
      const form = new FormData();
      for (const d of dateien) form.append('dateien', d);
      return anfrage<{ angelegt: number; posten: Eingangsposten[]; kategorien: Kategorie[]; kiVerfuegbar: boolean }>(
        `/pruefungen/${pruefungId}/eingang`,
        { method: 'POST', body: form },
      );
    },
    lesen: (pruefungId: string) =>
      anfrage<{ posten: Eingangsposten[]; kategorien: Kategorie[]; kiVerfuegbar: boolean }>(`/pruefungen/${pruefungId}/eingang`),
    uebernehmen: (
      pruefungId: string,
      entscheidungen: { id: string; aktion: EingangAktion; kategorieId?: string | null; dateiname?: string | null }[],
      salden: boolean,
    ) =>
      anfrage<{ unterlagen: number; zurueckgehalten: number; verworfen: number; salden: number; meldungen: string[]; verbleibend: number }>(
        `/pruefungen/${pruefungId}/eingang/uebernehmen`,
        { method: 'POST', body: JSON.stringify({ entscheidungen, salden }) },
      ),
    leeren: (pruefungId: string) => anfrage<{ ok: true }>(`/pruefungen/${pruefungId}/eingang`, { method: 'DELETE' }),
    dateiUrl: (pruefungId: string, postenId: string) => {
      const token = zugangToken();
      return `/api/pruefungen/${pruefungId}/eingang/${postenId}/datei${token ? `?zugang=${encodeURIComponent(token)}` : ''}`;
    },
  },

  // ── Saldenabgleich (Schritt 1)

  salden: (id: string) =>
    DEMO ? demoApi.salden() : anfrage<Saldenzeile[]>(`/pruefungen/${id}/salden`),

  saldenSpeichern: (id: string, zeilen: Partial<Saldenzeile>[]) =>
    DEMO
      ? demoApi.saldenSpeichern(zeilen)
      : anfrage<Saldenzeile[]>(`/pruefungen/${id}/salden`, {
          method: 'PUT',
          body: JSON.stringify({ zeilen }),
        }),

  // ── Importpaket (Belege ohne iDWELL)

  importHochladen: (pruefungId: string, datei: File) => {
    const form = new FormData();
    form.append('datei', datei);
    return anfrage<{
      belege: number;
      mitDatei: number;
      buchungen: number;
      fehlendeDateien: number;
      hinweise: string[];
      objekt: string | null;
    }>(`/pruefungen/${pruefungId}/import`, { method: 'POST', body: form });
  },

  kommentare: (id: string) =>
    DEMO ? demoApi.kommentare() : anfrage<Kommentar[]>(`/pruefungen/${id}/kommentare`),

  kommentarAnlegen: (
    id: string,
    daten: {
      belegId?: string;
      transaktionId?: string;
      dokumentId?: string;
      schritt?: number;
      elternId?: string;
      text: string;
      markierung?: PdfRechteck;
    },
  ) =>
    DEMO
      ? demoApi.kommentarAnlegen(daten)
      : anfrage<{ id: string }>(`/pruefungen/${id}/kommentare`, {
          method: 'POST',
          body: JSON.stringify(daten),
        }),

  kommentarErledigen: (kommentarId: string) =>
    DEMO
      ? demoApi.kommentarErledigen(kommentarId)
      : anfrage<{ ok: true }>(`/kommentare/${kommentarId}/erledigt`, { method: 'PATCH' }),

  abschliessen: (
    id: string,
    urteil: 'ohne_einwand' | 'mit_vorbehalt',
    anmerkung: string,
    bericht?: { ort?: string; entlastungEmpfohlen?: boolean },
  ) =>
    DEMO
      ? demoApi.abschliessen(urteil, anmerkung)
      : anfrage<{ ok: true; offeneRueckfragen: number }>(`/pruefungen/${id}/abschluss`, {
          method: 'POST',
          body: JSON.stringify({ urteil, anmerkung, ...(bericht ?? {}) }),
        }),

  // ─────────────────────────────────────────────────────────
  // Verwaltungsseite — angemeldete Benutzer
  // ─────────────────────────────────────────────────────────

  konto: {
    lesen: () => anfrage<Konto>('/konto'),
    anmelden: (email: string, passwort: string) =>
      anfrage<Konto>('/konto/anmelden', { method: 'POST', body: JSON.stringify({ email, passwort }) }),
    abmelden: () => anfrage<{ ok: true }>('/konto/abmelden', { method: 'POST' }),
    passwortVergessen: (email: string) =>
      anfrage<{ ok: true; meldung: string }>('/konto/passwort-vergessen', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    passwortLinkPruefen: (token: string) =>
      anfrage<{ email: string; name: string }>(`/konto/passwort/${encodeURIComponent(token)}`),
    passwortSetzen: (token: string, passwort: string) =>
      anfrage<Konto>(`/konto/passwort/${encodeURIComponent(token)}`, {
        method: 'POST',
        body: JSON.stringify({ passwort }),
      }),
    passwortAendern: (altesPasswort: string, neuesPasswort: string) =>
      anfrage<{ ok: true }>('/konto/passwort', {
        method: 'POST',
        body: JSON.stringify({ altesPasswort, neuesPasswort }),
      }),
  },

  mandant: {
    lesen: () =>
      anfrage<{ mandant: Mandant; kategorien: Kategorie[]; benutzer: Mitarbeiter[]; mailBereit: boolean }>(
        '/mandant',
      ),
    aendern: (daten: { name?: string; kurzname?: string | null }) =>
      anfrage<{ mandant: Mandant }>('/mandant', { method: 'PATCH', body: JSON.stringify(daten) }),
    idwellSetzen: (apiKey: string, basisUrl?: string) =>
      anfrage<{ mandant: Mandant; verbunden: { mandant: string; buchhaltung: boolean } }>('/mandant/idwell', {
        method: 'PUT',
        body: JSON.stringify({ apiKey, basisUrl }),
      }),
    idwellLoeschen: () => anfrage<{ mandant: Mandant }>('/mandant/idwell', { method: 'DELETE' }),
    idwellPruefen: () =>
      anfrage<{ ok: boolean; mandant: string; buchhaltung: boolean }>('/mandant/idwell/pruefen', {
        method: 'POST',
      }),
    kategorien: () => anfrage<Kategorie[]>('/mandant/kategorien'),
    kategorieAnlegen: (daten: { bezeichnung: string; hinweis?: string; schritt: number; pflicht?: boolean }) =>
      anfrage<Kategorie>('/mandant/kategorien', { method: 'POST', body: JSON.stringify(daten) }),
    kategorieAendern: (id: string, daten: Partial<Pick<Kategorie, 'bezeichnung' | 'hinweis' | 'schritt' | 'pflicht' | 'aktiv' | 'reihenfolge'>>) =>
      anfrage<Kategorie>(`/mandant/kategorien/${id}`, { method: 'PATCH', body: JSON.stringify(daten) }),
    kategorieLoeschen: (id: string) =>
      anfrage<{ ok: true }>(`/mandant/kategorien/${id}`, { method: 'DELETE' }),
    kategorienOrdnen: (ids: string[]) =>
      anfrage<Kategorie[]>('/mandant/kategorien/reihenfolge', { method: 'PUT', body: JSON.stringify({ ids }) }),
    benutzer: () => anfrage<Mitarbeiter[]>('/mandant/benutzer'),
    benutzerEinladen: (email: string, name: string) =>
      anfrage<{ link: string; gueltigBis: string; verschickt: boolean; meldung: string }>('/mandant/benutzer', {
        method: 'POST',
        body: JSON.stringify({ email, name }),
      }),
    benutzerLink: (id: string) =>
      anfrage<{ link: string; gueltigBis: string; verschickt: boolean; meldung: string }>(
        `/mandant/benutzer/${id}/link`,
        { method: 'POST' },
      ),
    benutzerAendern: (id: string, daten: { aktiv?: boolean; name?: string }) =>
      anfrage<Mitarbeiter[]>(`/mandant/benutzer/${id}`, { method: 'PATCH', body: JSON.stringify(daten) }),
    pruefungen: () => anfrage<PruefungKurz[]>('/mandant/pruefungen'),
    mitglieder: (pruefungId: string) => anfrage<Mitglied[]>(`/mandant/pruefungen/${pruefungId}/mitglieder`),
    mitgliedEntfernen: (pruefungId: string, mitgliedId: string) =>
      anfrage<{ ok: true }>(`/mandant/pruefungen/${pruefungId}/mitglieder/${mitgliedId}`, { method: 'DELETE' }),
    pruefungLoeschen: (pruefungId: string) =>
      anfrage<{ ok: true }>(`/mandant/pruefungen/${pruefungId}`, { method: 'DELETE' }),
  },

  admin: {
    mandanten: () => anfrage<(Mandant & { benutzer: number; pruefungen: number })[]>('/admin/mandanten'),
    mandantAnlegen: (daten: {
      name: string;
      kurzname?: string;
      apiKey?: string;
      basisUrl?: string;
      adminEmail?: string;
      adminName?: string;
    }) =>
      anfrage<{
        mandant: Mandant;
        zugang: { link: string; gueltigBis: string; verschickt: boolean; meldung: string } | null;
      }>('/admin/mandanten', { method: 'POST', body: JSON.stringify(daten) }),
    mandantAendern: (id: string, daten: { name?: string; kurzname?: string | null; aktiv?: boolean }) =>
      anfrage<Mandant>(`/admin/mandanten/${id}`, { method: 'PATCH', body: JSON.stringify(daten) }),
    benutzer: (mandantId: string) => anfrage<Mitarbeiter[]>(`/admin/mandanten/${mandantId}/benutzer`),
    benutzerEinladen: (mandantId: string, email: string, name: string) =>
      anfrage<{ link: string; gueltigBis: string; verschickt: boolean; meldung: string }>(
        `/admin/mandanten/${mandantId}/benutzer`,
        { method: 'POST', body: JSON.stringify({ email, name }) },
      ),
    benutzerLink: (id: string) =>
      anfrage<{ link: string; gueltigBis: string; verschickt: boolean; meldung: string }>(
        `/admin/benutzer/${id}/link`,
        { method: 'POST' },
      ),
    betreiber: () => anfrage<Mitarbeiter[]>('/admin/betreiber'),
    betreiberEinladen: (email: string, name: string) =>
      anfrage<{ link: string; gueltigBis: string; verschickt: boolean; meldung: string }>('/admin/betreiber', {
        method: 'POST',
        body: JSON.stringify({ email, name }),
      }),
  },
};

// ─────────────────────────────────────────────────────────────
// Formatierung
// ─────────────────────────────────────────────────────────────

/** Beträge wie auf deutschen Abrechnungen: 517.976,68 € — Punkt als Tausenderzeichen. */
export function eur(wert: number | null | undefined): string {
  if (wert === null || wert === undefined) return '—';
  return wert.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '\u00a0€';
}

export function datum(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [jahr, monat, tag] = iso.slice(0, 10).split('-');
  return `${tag}.${monat}.${jahr}`;
}

export function datumKurz(iso: string | null | undefined): string {
  if (!iso) return '';
  const [, monat, tag] = iso.slice(0, 10).split('-');
  return `${tag}.${monat}.`;
}

/** Restlaufzeit bis zur Frist, in ganzen Tagen. */
export function tageBis(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / 86_400_000);
}
